import { useContext, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Settings, Shield, User as UserIcon, LogOut, LogIn } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import { useSiteConfig } from '../../contexts/SiteConfigContext';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '../ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Separator } from '../ui/separator';
import { cn } from '../../lib/utils';
import kzcompLogo from '../../kzcomp.png';

const initialsFrom = (name, email) => {
  const source = (name || email || '').trim();
  if (!source) return '?';
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  const letters = parts.length >= 2 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2);
  return letters.toUpperCase();
};

const Navbar = () => {
  const { currentUser, userProfile, isAdmin, logout } = useContext(AuthContext);
  const { config } = useSiteConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = config.navigation.links;

  const displayName =
    userProfile?.displayName || userProfile?.username || currentUser?.email || 'Profile';
  const initials = initialsFrom(displayName, currentUser?.email);

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/60 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="mx-auto flex h-16 max-w-screen-2xl items-center gap-6 px-4 sm:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <img src={kzcompLogo} alt="KZCOMP" className="h-8 w-auto" />
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {navLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              className={({ isActive }) =>
                cn(
                  'relative rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                )
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  {isActive && (
                    <span className="absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-primary" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {currentUser ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label="User menu"
                >
                  <Avatar className="h-9 w-9">
                    {userProfile?.photoURL && <AvatarImage src={userProfile.photoURL} alt="" />}
                    <AvatarFallback>{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="flex flex-col gap-0.5">
                  <span className="truncate text-sm">{displayName}</span>
                  <span className="truncate text-xs font-normal text-muted-foreground">
                    {currentUser.email}
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => navigate(`/user/${currentUser.uid}`)}>
                  <UserIcon className="h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate('/settings')}>
                  <Settings className="h-4 w-4" />
                  Account settings
                </DropdownMenuItem>
                {isAdmin() && (
                  <DropdownMenuItem onSelect={() => navigate('/admin/dashboard')}>
                    <Shield className="h-4 w-4" />
                    Admin dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={handleLogout}>
                  <LogOut className="h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild size="sm" variant="secondary" className="hidden md:inline-flex">
              <Link to="/login">
                <LogIn className="h-4 w-4" />
                Log in
              </Link>
            </Button>
          )}

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="flex h-full flex-col gap-6">
                <Link
                  to="/"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2"
                >
                  <img src={kzcompLogo} alt="KZCOMP" className="h-7 w-auto" />
                </Link>

                <nav className="flex flex-col gap-1">
                  {navLinks.map((link) => {
                    const active = location.pathname === link.path;
                    return (
                      <SheetClose asChild key={link.path}>
                        <Link
                          to={link.path}
                          className={cn(
                            'rounded-md px-3 py-2 text-sm font-medium transition-colors',
                            active
                              ? 'bg-secondary text-foreground'
                              : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
                          )}
                        >
                          {link.label}
                        </Link>
                      </SheetClose>
                    );
                  })}
                </nav>

                <Separator />

                <div className="mt-auto flex flex-col gap-2">
                  {currentUser ? (
                    <>
                      <SheetClose asChild>
                        <Button asChild variant="secondary" className="justify-start">
                          <Link to={`/user/${currentUser.uid}`}>
                            <UserIcon className="h-4 w-4" />
                            Profile
                          </Link>
                        </Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button asChild variant="secondary" className="justify-start">
                          <Link to="/settings">
                            <Settings className="h-4 w-4" />
                            Account settings
                          </Link>
                        </Button>
                      </SheetClose>
                      {isAdmin() && (
                        <SheetClose asChild>
                          <Button asChild variant="secondary" className="justify-start">
                            <Link to="/admin/dashboard">
                              <Shield className="h-4 w-4" />
                              Admin dashboard
                            </Link>
                          </Button>
                        </SheetClose>
                      )}
                      <Button
                        variant="outline"
                        className="justify-start"
                        onClick={() => {
                          setMobileOpen(false);
                          handleLogout();
                        }}
                      >
                        <LogOut className="h-4 w-4" />
                        Log out
                      </Button>
                    </>
                  ) : (
                    <SheetClose asChild>
                      <Button asChild>
                        <Link to="/login">
                          <LogIn className="h-4 w-4" />
                          Log in
                        </Link>
                      </Button>
                    </SheetClose>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
